import React, { useState } from 'react';
import RoutineSkillsEditor, { toRows, toPayloadSkills } from './RoutineSkillsEditor';
import './GymnastRoutines.css';

// Create or edit a level routine with its skills in one form, using the same
// skill list as gymnast routines. `routine` is null when creating. onSave gets
// the API payload and rejects on failure, leaving the form as it was.
export default function LevelRoutineEditor({ routine, levelLabel, availableSkills, apparatus = 'TRA', onSave, onCancel }) {
  const isNew = !routine;
  // On a double mini level a routine is a pass
  const word = apparatus === 'DMT' ? 'pass' : 'routine';
  const [name, setName] = useState(routine?.name || '');
  const [description, setDescription] = useState(routine?.description || '');
  const [isAlternative, setIsAlternative] = useState(Boolean(routine?.isAlternative));
  const [dmtStart, setDmtStart] = useState(routine?.dmtStart || 'mount');
  const [order, setOrder] = useState(routine?.order ? String(routine.order) : '');
  const [rows, setRows] = useState(() => (routine ? toRows(routine.skills || []) : []));
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: name.trim() || null,
      description: description.trim() || null,
      isAlternative,
      ...(apparatus === 'DMT' ? { dmtStart } : {}),
      skills: toPayloadSkills(rows),
    };
    if (order) payload.order = Number(order);
    setSaving(true);
    try {
      await onSave(payload);
    } catch (_err) {
      // The page shows the error; keep what was typed
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay gymnast-routine-editor__overlay">
      <div className="modal gymnast-routine-editor">
        <div className="modal-header">
          <h3>{isNew ? `Add ${word}` : `Edit ${word}`}{levelLabel ? ` · ${levelLabel}` : ''}</h3>
          <button type="button" onClick={onCancel} className="close-button" aria-label="Close">×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="gymnast-routine-editor__row">
            <div className="form-group">
              <label htmlFor="level-routine-name">Name</label>
              <input
                id="level-routine-name"
                className="form-control"
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
                placeholder={apparatus === 'DMT' ? 'e.g. Pass 1' : 'e.g. Routine 1'}
              />
            </div>
            <div className="form-group">
              <label htmlFor="level-routine-order">Position</label>
              <input
                id="level-routine-order"
                className="form-control"
                type="number"
                min="1"
                value={order}
                onChange={(e) => setOrder(e.target.value)}
                placeholder={isNew ? 'At the end' : 'Leave blank to keep'}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="level-routine-description">Description</label>
            <textarea
              id="level-routine-description"
              className="form-control"
              rows={2}
              maxLength={500}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {apparatus === 'DMT' && (
            <div className="form-group">
              <label htmlFor="level-routine-start">First skill is done as a</label>
              <select id="level-routine-start" className="form-control" value={dmtStart} onChange={(e) => setDmtStart(e.target.value)}>
                <option value="mount">Mount</option>
                <option value="spotter">Spotter</option>
              </select>
            </div>
          )}

          <label className="level-routine-editor__check">
            <input type="checkbox" checked={isAlternative} onChange={(e) => setIsAlternative(e.target.checked)} />
            Alternative {word}
          </label>

          <RoutineSkillsEditor
            rows={rows}
            onChange={setRows}
            apparatus={apparatus}
            availableSkills={availableSkills}
          />

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : `Save ${word}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
